import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";

// Modules
import Reveal from "reveal.js";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import {
  DndContext,
  useDroppable,
  useSensors,
  useSensor,
  PointerSensor,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
// import RevealDist from 'reveal.js/dist/reveal.esm';
import Notes from "reveal.js/plugin/notes/notes";

// App
import { filesystem, subdomain, assetOrigin, presenterNotesEnabled } from "../../config";
import axios from "axios";
import { getOnlineNode } from "../../core/getNode";
import { AssetSigsProvider } from "../../core/assetSigs";
import Slide from "./partials/slide/view/slide";
import RevealControls from "./partials/revealControls";
import {
  jsonParser,
  runAnimation,
  runFragment,
  addWidescreen,
  pauseFrameVideos,
  iframeClickEvents,
} from "../../utils/helpers";
import CompanyappLoader from "../../partials/companyappLoader";
import AudioPrompt from "../../partials/audioPrompt";
import { logCustomEvent } from "../../core/analytics";
import { ViewContextProvider, useViewContext } from "./viewPresentationContext";

// UI components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import { Box, Alert } from "@mui/material";
import { debounce } from "lodash";

let isSpeakerView = false;

try {
  isSpeakerView = window.parent?.name === "reveal.js - Notes";
} catch {
  // Cross-origin parent → ignore
}

// Captured once at import so the iframe-mode patch below can never capture an
// already-patched (no-op) version and make the loss permanent.
const originalScrollIntoView = Element.prototype.scrollIntoView;

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

function ViewPresentationInner(props) {
  const [isLoading, setLoading] = useState(true);
  const [additionalLoader, setAdditionalLoader] = useState(true);
  const [isError, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "An error occurred. Please try again or contact system administrator.",
  );

  const { setSlideAudioData } = useViewContext();
  const isInitialRef = useRef(true);

  let [elementDimensions, setElementDimensions] = useState({
    width: 130,
    height: 30,
  });
  let [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // controls delta
  let [x, setX] = useState(window.innerWidth - (elementDimensions.width + 10));
  let [y, setY] = useState(30);

  // hooks
  const dispatch = useDispatch();
  const params = useParams();
  const [searchParams] = useSearchParams();

  // redux
  const user = useSelector((state) => state).authReducer.user;
  const permissions = useSelector((state) => state).permissionsReducer
    .permissions;

  // refs
  const revealRef = useRef(null);
  // Guards async callbacks (fetches, reveal init, timers) from calling
  // setState after the user navigates away — the source of the "state update
  // on an unmounted component" leak warnings.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
      // The iframe-mode nav patch no-ops scrollIntoView and restores it on the
      // next frame; unmounting between the two would leave it dead app-wide.
      Element.prototype.scrollIntoView = originalScrollIntoView;
    },
    [],
  );

  const [presentation, setPresentation] = useState(null);
  const [audio, setAudio] = useState(null);
  const [backgroundColor, setBackgroundColor] = useState("");
  const [reveal, setReveal] = useState(null);
  const [indexh, setIndexH] = useState(0);
  const [showControls, setShowControls] = useState(true);
  // const [indexv, setIndexV] = useState(0);

  /** Presentation audio */
  const [slideHasAudioPrompt, setSlideHasAudioPrompt] = useState(false);

  const hash = searchParams.get("hash");
  const slideIds = searchParams.get("slide_ids");
  const noControls = searchParams.get("nocontrols");
  const previewChanges = searchParams.get("previewChanges");
  const previewChangesSlideId = searchParams.get("previewChangesSlideId");
  const previewChangesAnalysisId = searchParams.get("previewChangesAnalysisId");

  // droppable
  const { setNodeRef } = useDroppable({
    id: "droppable",
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const handleSlideAudioPrompt = useCallback(() => {
    setSlideHasAudioPrompt(true);
  }, []);

  // Toggle nav sidebar and getcontent
  useEffect(() => {
    dispatch({
      type: "TOGGLE_NAVIGATION",
      payload: {
        showNavigation: false,
        manualToggle: true,
      },
    });
    getContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resize listener
  useEffect(() => {
    setDimensions(getWindowDimensions()); // Necessary to make sure dimensions are set upon initial load

    function handleResize() {
      setDimensions(getWindowDimensions());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Mouse move listener. Tracked in a local so a mousemove storm doesn't
  // schedule a state update (and a render) per event.
  useEffect(() => {
    let visible = true;
    const hideRevealControls = debounce(() => {
      visible = false;
      setShowControls(false);
    }, 5000);

    const handleMouseMove = () => {
      if (!visible) {
        visible = true;
        setShowControls(true);
      }
      hideRevealControls();
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      hideRevealControls.cancel();
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === "next") {
        revealRef.current.next();
        return;
      }

      if (event.data === "previous") {
        revealRef.current.prev();
        return;
      }

      if (event.data?.type === "SLIDE_READY") {
        const iframe = [...document.querySelectorAll("iframe")].find(
          (f) => f.contentWindow === event.source,
        );

        if (iframe) {
          iframe.dataset.slideReady = "true";

          const currentSlide = revealRef.current?.getCurrentSlide();
          const currentIframe = currentSlide?.querySelector("iframe.advanced-slide");

          // If this iframe is on the active slide, tell it to run
          if (currentIframe === iframe) {
            iframe.contentWindow.postMessage({ type: "RUN_ANIMATION" }, "*");
          }
        }

        return;
      }

      if (event.data?.type === "REGISTER_FRAGMENTS") {
        const iframe = [...document.querySelectorAll("iframe")].find(
          (f) => f.contentWindow === event.source,
        );

        registerFragments(iframe, event.data.count)

        return;
      }

    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [revealRef]);

  // Initialize Plyr on slide videos, and tear the instances down on unmount.
  // Fixes vs the previous version, which leaked a player per video:
  //  - Select `video.plyr`, not `.plyr`: Plyr wraps each <video> in a .plyr
  //    container, so a bare ".plyr" selector re-matched the wrapper and
  //    double-initialised every video.
  //  - Mark data-initialized BEFORE constructing Plyr so the observer can't
  //    re-enter on Plyr's own DOM mutations.
  //  - Keep a registry and destroy() every instance on cleanup (the old code
  //    never destroyed them — listeners + detached DOM leaked for the session).
  //  - Scope the observer to the slides container instead of document.body so
  //    it doesn't re-scan the whole deck on every unrelated mutation.
  useEffect(() => {
    if (!reveal) return;

    const slidesRoot = document.querySelector(".reveal .slides");
    if (!slidesRoot) return;

    const players = [];

    const initVideos = () => {
      slidesRoot
        .querySelectorAll("video.plyr:not([data-initialized])")
        .forEach((video) => {
          video.setAttribute("data-initialized", "true");
          players.push(
            new window.Plyr(video, {
              controls: [
                "play-large",
                "play",
                "progress",
                "current-time",
                "mute",
                "volume",
                "fullscreen",
              ],
            }),
          );
        });
    };

    const observer = new MutationObserver(initVideos);
    observer.observe(slidesRoot, { childList: true, subtree: true });
    initVideos();

    return () => {
      observer.disconnect();
      players.forEach((player) => {
        try {
          player.destroy();
        } catch (e) {
          /* element already removed */
        }
      });
    };
  }, [reveal]);

  // Slide jump
  useEffect(() => {
    if (reveal) {
      revealRef.current = reveal;
    }

    window.goToSlide = goToSlide;
    return () => {
      window.goToSlide = undefined;
    };
    // eslint-disable-next-line
  }, [reveal]);

  // Initialize reveal
  useEffect(() => {
    if (
      presentation &&
      presentation?.slides &&
      presentation.slides.length > 0 &&
      !reveal
    ) {
      const previousTitle = document.title;

      if (presentation?.title) {
        document.title = `${presentation.title} (${presentation.id}) – ${previousTitle}`;
      }

      // Small delay to ensure slides are rendered
      let cancelled = false;
      const timer = setTimeout(() => {
        // The At a glance layouts read their hotel live and fill in after
        // their own fetch. decktape starts capturing as soon as Reveal
        // reports ready, so in the screenshot app hold initialisation until
        // those have settled - otherwise a thumbnail catches the cover rather
        // than the slide. Interactive viewing is never delayed: the blocks
        // hydrate in place while the deck is already usable.
        const isCaptureHost =
          typeof window !== "undefined" &&
          window.location?.hostname?.includes("screenshot");

        const ready =
          isCaptureHost && typeof window.aagReady === "function"
            ? window.aagReady()
            : Promise.resolve();

        ready.then(() => {
          if (!cancelled) initReveal();
        });
      }, 100);

      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentation, reveal]);

  const presentationJson = useMemo(
    () => jsonParser(presentation?.json),
    [presentation],
  );

  // Check if any slide in the presentation has audio (for the prompt)
  useEffect(() => {
    if (isSpeakerView || !presentation?.slides?.length) {
      return;
    }

    if (presentationJson?.slideAudio) {
      const hasAnySlideAudio = presentation.slides.some(
        (slide) => presentationJson.slideAudio[slide.id],
      );

      if (hasAnySlideAudio) {
        handleSlideAudioPrompt();
      }
    }
  }, [presentation, presentationJson, handleSlideAudioPrompt]);

  // Monitor current slide audio
  useEffect(() => {
    if (isSpeakerView || !presentation?.slides?.length) {
      return;
    }

    const currentSlide = presentation?.slides?.[indexh];

    if (currentSlide && presentationJson?.slideAudio) {
      const slideAudio = presentationJson.slideAudio[currentSlide.id];
      setSlideAudioData(slideAudio ? slideAudio : null);
    } else {
      setSlideAudioData(null);
    }
  }, [indexh, presentation, presentationJson, setSlideAudioData]);

  const goToSlide = (slideId) => {
    let slideNum = presentation?.slides.map((e) => e.id).indexOf(slideId);
    reveal.slide(parseInt(slideNum));
  };

  const getContent = () => {
    if (params?.id) {
      if (props.preview) {
        getContentFromApi();
      } else {
        checkAuth();
      }
    } else {
      setError(true);
      setAdditionalLoader(false);
    }
  };

  const getContentFromApi = () => {
    // let path = `api/presentation/${params.id}?status=${props.preview ? 'false' : 'true'}&slide_status=true`;
    let path = `api/presentation/${params.id}?status=false&slide_status=true`;

    if (slideIds) {
      path += `&slide_ids=${slideIds}`;
    }

    if (hash) {
      path += `&auHash=${hash}`;
    }

    getOnlineNode(path)
      .then((response) => {
        if (!mountedRef.current) return;
        if (response.data) {
          const json = response.data.json;

          if (json?.audio) {
            setAudio(json.audio);
          }

          if (json?.backgroundColor) {
            setBackgroundColor(json.backgroundColor);
          }

          if (previewChanges && previewChangesSlideId) {
            let path = `/api/v2/ai/slide/responses?slide_id=${previewChangesSlideId}&show=1`;

            if (previewChangesAnalysisId) {
              path += `&analysis_id=${previewChangesAnalysisId}`;
            }

            getOnlineNode(path)
              .then((aIresponseData) => {
                if (!mountedRef.current) return;
                if (aIresponseData?.data?.rows?.length) {
                  const aiResponse = aIresponseData.data.rows[0];

                  try {
                    const parsed = JSON.parse(aiResponse.response);
                    let presData = { ...response.data };
                    let slides = [];

                    // Convert previewChangesSlideId to number for comparison
                    const targetSlideId = parseInt(previewChangesSlideId, 10);

                    presData.slides.forEach((slide) => {
                      // Use loose equality or convert both to same type
                      if (parseInt(slide.id, 10) === targetSlideId) {
                        slides.push({
                          ...slide,
                          // Ensure slide_json is a string for jsonParser
                          slide_json: typeof parsed.suggested_json === 'string'
                            ? parsed.suggested_json
                            : JSON.stringify(parsed.suggested_json),
                        });
                      }
                    });

                    if (slides.length > 0) {
                      presData.slides = slides;
                      presData.slide_length = slides.length;
                      setPresentation(presData);
                    } else {
                      // No matching slide found, show original presentation
                      console.error("Preview slide not found in presentation");
                      setPresentation(response.data);
                    }
                  } catch (e) {
                    console.error("Error parsing AI response:", e);
                    setPresentation(response.data);
                  }
                } else {
                  setPresentation(response.data);
                }
              })
              .catch((error) => {
                if (!mountedRef.current) return;
                console.error("Error fetching AI analysis:", error);
                setPresentation(response.data);
              })
              .finally(() => {
                if (!mountedRef.current) return;
                setError(false);
                setLoading(false);
              });
          } else {
            setPresentation(response.data);

            setError(false);
            setLoading(false);
          }
        } else {
          setError(true);
          setAdditionalLoader(false);
        }
      })
      .catch((_error) => {
        if (!mountedRef.current) return;
        if (_error?.response?.status === 403) {
          setErrorMessage("You do not have access to view this presentation.");
        }

        setLoading(false);
        console.log('error fetching presentation', _error);
        setError(true);
        setAdditionalLoader(false);
      });
  };

  const getContentFromStaticFS = () => {
    let path = `${assetOrigin}/presentation_book.php?presentation_id=${params.id}&subdomain=${subdomain}&filesystem=${filesystem}`;

    if (slideIds) {
      path += `&slide_ids=${slideIds}`;
    }

    // assetOrigin (not assetBase): presentation_book.php lives at the assets
    // ROOT and is symlinked to the build docroot (exactly like file_handler.php),
    // NOT duplicated inside the <subdomain> dir. So it is reached at the bare
    // docroot /presentation_book.php — same-origin on deployed hosts
    // (assetOrigin = "") and off the files server on localhost
    // (assetOrigin = filesystem_root). Prefixing the subdomain
    // (/<subdomain>/presentation_book.php) 404s because the handler is not
    // inside the asset tree. Plain axios.get (not getExternal — that's pinned to
    // the iceportal baseURL, which would swallow the now-relative same-origin
    // path). withCredentials false because it's referer-authed, not
    // cookie-authed, and keeps the cross-origin (localhost → files server) fetch
    // out of credentialed CORS.
    axios
      .get(path, { withCredentials: false })
      .then((response) => {
        if (!mountedRef.current) return;
        if (response.data) {
          if (!user.current_user) {
            let hasMatch = false;
            if (response.data?.access_token.length > 0) {
              response.data.access_token.forEach((access_token) => {
                if (access_token.token === hash) {
                  hasMatch = true;
                }
              });
            } else {
              hasMatch = false;
            }

            // check hash
            if (hasMatch) {
              logCustomEvent(
                "read",
                {
                  title: response.data.title,
                  nid: response.data.id,
                },
                "Presentation",
              );
              renderPresentation(response);
            } else {
              setErrorMessage(
                "You do not have access to view this presentation.",
              );
              setError(true);
              setLoading(false);
              setAdditionalLoader(false);
            }
          } else {
            logCustomEvent(
              "read",
              {
                title: response.data.title,
                nid: response.data.id,
              },
              "Presentation",
            );

            renderPresentation(response);
          }
        } else {
          setError(true);
          setLoading(false);
          setAdditionalLoader(false);
        }
      })
      .catch((_error) => {
        if (!mountedRef.current) return;
        console.log('error fetching presentation', _error);
        setError(true);
        setLoading(false);
        setAdditionalLoader(false);
      });
  };

  const registerFragments = (iframe, count) => {

        if (!iframe) return;

        const slide = iframe.closest("section");
        if (!slide) return;


        if (!Number.isInteger(count) || count <= 0) return;

        slide
          .querySelectorAll('.fragment[data-iframe-fragment="true"]')
          .forEach((el) => el.remove());

        // Append invisible fragments after the iframe
        for (let i = 1; i <= count; i++) {
          const fragment = document.createElement("span");
          fragment.className = "fragment";
          fragment.dataset.iframeFragment = "true";
          fragment.dataset.step = String(i);
          fragment.style.display = "none";

          iframe.insertAdjacentElement("beforebegin", fragment);
        }

        return;
      }

  const renderPresentation = (response) => {
    const json = jsonParser(response.data.json);

    if (json?.audio) {
      setAudio(json.audio);
    }

    if (json?.backgroundColor) {
      setBackgroundColor(json.backgroundColor);
    }

    setPresentation(response.data);
    setError(false);
    setLoading(false);
  };

  const checkAuth = () => {
    if (hash) {
      getContentFromStaticFS();
    } else {
      if (user.current_user) {
        getContentFromStaticFS();
      } else {
        setErrorMessage("You do not have access to view this presentation.");
        setLoading(false);
        setError(true);
        setAdditionalLoader(false);
      }
    }
  };

  const isInIframe = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();

  const initReveal = (index) => {
    let revealSettings = {
      width: 1366,
      height: 768,
      margin: 0,
      slideNumber: false,
      help: false,
      transition: "slide",
      progress: false,
      hashOneBasedIndex: true,
      respondToHashChanges: isInIframe ? false : true,
      hash: isInIframe ? false : true,
      history: isInIframe ? false : true,
      controls: false,
      controlsTutorial: false,
      overview: false,
      fragmentInURL: isInIframe ? false : true,
      autoSlide: 0,
      viewDistance: 10,
      preloadIframes: true,
      maxScale: 5.0,
      embedded: isInIframe,
    };

    if (!hash && !isInIframe && permissions.access_note_features && presenterNotesEnabled) {
      revealSettings.plugins = [Notes];
    }

    let deck = new Reveal(revealSettings);

    deck
      .initialize()
      .then(() => {
        if (!mountedRef.current) return;
        // Drop the overlay once the deck's first layout has painted (double
        // rAF) instead of a flat 2.5s every load. Advanced slides still in
        // flight are handled by checkAdvancedSlide's own polling. The timeout
        // is a fallback for background tabs, where rAF never fires.
        let loaderCleared = false;
        const clearLoader = () => {
          if (loaderCleared || !mountedRef.current) return;
          loaderCleared = true;
          setAdditionalLoader(false);
          checkAdvancedSlide(deck.getCurrentSlide());
        };
        requestAnimationFrame(() => {
          requestAnimationFrame(clearLoader);
        });
        setTimeout(clearLoader, 2500);

        setReveal(deck);

        if (index) {
          deck.slide(index);
        }

        const indices = deck.getIndices();

        if (indices && indices.h) {
          setIndexH(indices.h);
        }

        // Only patch navigation if in iframe
        if (isInIframe) {
          ["prev", "next", "slide"].forEach((method) => {
            const original = deck[method].bind(deck);
            deck[method] = function (...args) {
              Element.prototype.scrollIntoView = function () {};
              return original(...args);
            };
          });

          deck.on("slidechanged", (event) => {
            requestAnimationFrame(() => {
              Element.prototype.scrollIntoView = originalScrollIntoView;
            });

            setIndexH(event.indexh);
            leaveAdvancedSlide(event.previousSlide);
            // checkAdvancedSlide already sweeps every advanced iframe.
            checkAdvancedSlide(deck.getCurrentSlide());
          });
        } else {
          deck.on("slidechanged", (event) => {
            setIndexH(event.indexh);
            leaveAdvancedSlide(event.previousSlide);
            checkAdvancedSlide(deck.getCurrentSlide());
          });
        }

        deck.on("slidetransitionend", (event) => {
          const allIframes = document.querySelectorAll(
            ".slides iframe.advanced-slide",
          );
          allIframes.forEach((iframe) => {
            addWidescreen(iframe);
          });
        });
        deck.on("fragmentshown", (event) => {
          runAdvancedSlideFragment(
            deck.getCurrentSlide(),
            event.fragment.dataset.step,
          );
        });
        deck.on("fragmenthidden", (event) => {
          runAdvancedSlideFragment(
            deck.getCurrentSlide(),
            event.fragment.dataset.step - 1,
          );
        });
      })
      .catch((error) => {
        setAdditionalLoader(false);
      });
  };

  // Videos keep playing (audible) on a slide the presenter has left, so stop
  // them on the way out. The slide replays its entrance on return, so reset to
  // the start rather than leaving a half-played video paused mid-frame.
  const leaveAdvancedSlide = (previousSlide) => {
    let prevIframe;
    try {
      prevIframe = previousSlide?.querySelector("iframe.advanced-slide");
    } catch {
      //
    }
    pauseFrameVideos(prevIframe);
  };

  const checkAdvancedSlide = (currentSlide) => {
    const allIframes = document.querySelectorAll(
      ".slides iframe.advanced-slide",
    );
    allIframes.forEach((iframe) => {
      addWidescreen(iframe);
    });

    let currentIframe;

    if (currentSlide) {
      try {
        currentIframe = currentSlide.querySelector("iframe.advanced-slide");
      } catch {
        //
      }
    }

    if (currentIframe) {
      if (currentIframe.dataset.slideReady === "true") {
        // New slide with message passing - tell it to run directly
        currentIframe.contentWindow.postMessage({ type: "RUN_ANIMATION" }, "*");
      } else {
        // Legacy slide - fall back to polling
        runAnimation(currentIframe);
      }
      iframeClickEvents(currentIframe);
    }
  };

  // A frame can finish loading long after the slidechanged sweep that asked
  // for it: a thumbnail-scroller jump loads its target from cold, and reveal's
  // unload() only drops src, so until the new document commits the frame still
  // holds the previous one. The frame's own load event is the one signal that
  // always lands on the document that actually needs the class + animation.
  useEffect(() => {
    if (!reveal) return;

    const slidesRoot = document.querySelector(".reveal .slides");
    if (!slidesRoot) return;

    // load doesn't bubble, but capture still runs through ancestors, so one
    // listener covers every frame in the deck.
    const handleFrameLoad = (event) => {
      const iframe = event.target;
      if (
        !(iframe instanceof HTMLIFrameElement) ||
        !iframe.classList.contains("advanced-slide")
      ) {
        return;
      }

      addWidescreen(iframe);

      // Legacy frames only. A new-style frame announces itself with
      // SLIDE_READY - which it posts before load, since load also waits on
      // subresources - and that handler already runs the animation when the
      // frame is the current slide. Posting here too would run it twice.
      if (iframe.dataset.slideReady === "true") return;

      const currentSlide = reveal.getCurrentSlide();
      if (!currentSlide || !currentSlide.contains(iframe)) return;

      runAnimation(iframe);
    };

    slidesRoot.addEventListener("load", handleFrameLoad, true);

    return () => {
      slidesRoot.removeEventListener("load", handleFrameLoad, true);
    };
  }, [reveal]);

  // Preload every slide iframe once the deck is interactive, nearest-ahead
  // of the current slide first, one frame at a time - same-process iframe
  // loads are what jank navigation, so the init burst becomes a background
  // trickle. The swap deliberately omits data-lazy-loaded: reveal's unload()
  // only reverts frames carrying that marker, so these load once and stay
  // warm for the session. AAG location maps are queued too - once hydration
  // has landed them - so the pin opens an already-booted map; other AAG-ME
  // panel iframes stay behind their panel-open swap.
  const preloadStartedRef = useRef(false);
  useEffect(() => {
    if (!reveal || additionalLoader || preloadStartedRef.current) return;
    preloadStartedRef.current = true;

    let cancelled = false;

    const withinViewDistance = (section) => {
      const horizontalSlides = reveal.getHorizontalSlides?.() || [];
      const index = horizontalSlides.indexOf(section);
      if (index === -1) return false;
      const current = reveal.getIndices()?.h || 0;
      const viewDistance = reveal.getConfig()?.viewDistance ?? 10;
      return Math.abs(current - index) < viewDistance;
    };

    const pending = [
      ...document.querySelectorAll(".reveal .slides iframe[data-src]"),
    ].filter(
      (iframe) =>
        !iframe.closest(".aag-me") || iframe.closest(".aag-me-map-wrap"),
    );

    // Always take the nearest pending frame AHEAD of the current slide,
    // wrapping past the end - a deck opened at slide 20 fills 21, 22, …
    // first and the slides behind come last. Re-evaluated per step so the
    // order follows the presenter if they move while the queue runs.
    const takeNearest = () => {
      const horizontalSlides = reveal.getHorizontalSlides?.() || [];
      const total = horizontalSlides.length;
      const current = reveal.getIndices()?.h || 0;
      let best = -1;
      let bestRank = Infinity;

      pending.forEach((iframe, i) => {
        const index = horizontalSlides.indexOf(iframe.closest("section"));
        const rank =
          index === -1 || !total
            ? Number.MAX_SAFE_INTEGER
            : (index - current + total) % total;
        if (rank < bestRank) {
          bestRank = rank;
          best = i;
        }
      });

      return best === -1 ? null : pending.splice(best, 1)[0];
    };

    // Reveal's window loads and the queue share ONE HTTP/2 connection to the
    // files host. A 54-advanced-slide deck saturates it the moment frames
    // overlap - the server resets streams (ERR_HTTP2_PROTOCOL_ERROR) and even
    // the current slide's assets die. So the queue is strictly serial: a
    // stalled frame is aborted, never left loading underneath its successors,
    // and the queue holds off while the user is actively navigating so
    // reveal's own in-window loads always win the connection.
    let lastNav = Date.now();
    const onNav = () => {
      lastNav = Date.now();
    };
    reveal.on("slidechanged", onNav);

    const NAV_QUIET_MS = 3000;
    const STALL_MS = 20000;

    let draining = false;

    const loadNext = () => {
      if (cancelled) return;

      const quietFor = Date.now() - lastNav;
      if (quietFor < NAV_QUIET_MS) {
        setTimeout(loadNext, NAV_QUIET_MS - quietFor);
        return;
      }

      const iframe = takeNearest();
      if (!iframe) {
        draining = false;
        return;
      }

      const src = iframe.getAttribute("data-src");
      // Reveal already loaded it (entered viewDistance) since the sweep.
      if (!src) {
        loadNext();
        return;
      }

      // Reveal only loads iframes inside display:block sections; a frame
      // booted inside an unloaded slide's display:none measures a 0x0
      // document and gets no animation frames, which breaks the slide's
      // runAnimation setup. Show the section for the load, hide it again
      // after - unless it entered the view window meanwhile. Non-present
      // slides are transformed off-canvas, so this is never visible.
      const section = iframe.closest("section");
      const wasHidden = section && section.style.display === "none";
      if (wasHidden) {
        section.style.display = "block";
      }

      let settled = false;

      // Grace period before re-hiding: boot work scheduled on rAF right
      // after load would be paused mid-setup by an instant display:none.
      const finish = () => {
        setTimeout(() => {
          if (wasHidden && !withinViewDistance(section)) {
            section.style.display = "none";
          }
          loadNext();
        }, 150);
      };

      const onLoad = () => {
        if (settled) return;
        settled = true;
        clearTimeout(stallTimer);
        iframe.removeEventListener("load", onLoad);
        finish();
      };

      const stallTimer = setTimeout(() => {
        if (settled) return;
        settled = true;
        iframe.removeEventListener("load", onLoad);
        // Abort the stalled load so it stops eating the connection, and
        // hand the frame back to reveal's lazy pass - unless the user has
        // reached its window, in which case reveal owns it now.
        if (!withinViewDistance(section)) {
          iframe.setAttribute("data-src", src);
          iframe.setAttribute("src", "about:blank");
        }
        finish();
      }, STALL_MS);

      iframe.addEventListener("load", onLoad);
      iframe.setAttribute("src", src);
      iframe.removeAttribute("data-src");
    };

    const drain = () => {
      if (draining || cancelled) return;
      draining = true;
      loadNext();
    };

    // Head start for the init burst: on a deep link reveal is already
    // loading every advanced frame inside the view window.
    const startTimer = setTimeout(drain, 5000);

    // The AAG bodies hydrate after the sweep above, so their map frames
    // are only collectable once the hotel payloads have landed.
    window.aagReady?.().then(() => {
      if (cancelled) return;
      let added = false;
      document
        .querySelectorAll(
          ".reveal .slides .map-section iframe[data-src], .reveal .slides .aag-me-map-wrap iframe[data-src]",
        )
        .forEach((iframe) => {
          if (!pending.includes(iframe)) {
            pending.push(iframe);
            added = true;
          }
        });
      if (added) drain();
    });

    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      reveal.off("slidechanged", onNav);
    };
  }, [reveal, additionalLoader]);

  const runAdvancedSlideFragment = (currentSlide, fragmentStep) => {
    let currentIframe;
    if (currentSlide) {
      try {
        currentIframe = currentSlide.querySelector("iframe.advanced-slide");
      } catch {
        //
      }
    }
    if (currentIframe) {
      runFragment(currentIframe, fragmentStep);
    }
  };

  function handleDragEnd(ev) {
    setX((x += ev.delta.x));
    setY((y += ev.delta.y));
  }

  // Stable props so the memoized RevealControls only re-renders when its
  // inputs actually change.
  const handleControlDimensions = useCallback((size) => {
    setElementDimensions((prev) =>
      prev.width === size.width && prev.height === size.height ? prev : size,
    );
    if (isInitialRef.current) {
      setX(window.innerWidth - (size.width + 30));
      setY(size.height + 10);
      isInitialRef.current = false;
    }
  }, []);

  const controlStyles = useMemo(
    () => ({
      left: `min(${dimensions.width - elementDimensions.width}px , ${x}px)`,
      top: `min(${dimensions.height - elementDimensions.height}px, ${y}px)`,
    }),
    [dimensions, elementDimensions, x, y],
  );

  // Slide computes its own json/theme_json/themeDirectory from the slide row;
  // only props its memo comparator actually checks are passed.
  const memoizedSlides = useMemo(() => {
    const isLive = props.preview ? false : true;

    if (
      !presentation ||
      !presentation?.slides ||
      presentation?.slides?.length < 1
    )
      return null;

    return presentation.slides.map((slide) => (
      <Slide
        key={slide.id}
        live={isLive}
        presentation_id={presentation?.id}
        slide={slide}
        slideNotes={presentationJson?.notes?.[slide.id] ?? null}
      />
    ));
  }, [props.preview, presentation, presentationJson]);

  if (isLoading) {
    return <CompanyappLoader />;
  } else {
    if (isError) {
      return (
        <Row className="h-100 justify-content-center align-self-center">
          <Col>
            <Box
              className="d-flex h-100 justify-content-center align-items-center align-content-center"
              sx={{ flexDirection: "column" }}
            >
              <Alert variant="filled" severity="error">
                {errorMessage}
              </Alert>
            </Box>
          </Col>
        </Row>
      );
    } else {
      return (
        <AssetSigsProvider presentation={presentation} isLive={!props.preview}>
        <main className="view-presentation screen">
          <Container
            fluid
            className="ps-container"
            style={backgroundColor ? { backgroundColor: backgroundColor } : {}}
          >
            <Row className="ps-row justify-content-center align-self-center">
              <Col
                className="ps-col p-0 ps-viewer"
                style={{ position: "relative" }}
              >
                <DndContext
                  onDragEnd={handleDragEnd}
                  sensors={sensors}
                  modifiers={[restrictToParentElement]}
                >
                  {additionalLoader && (
                    <Box
                      className="w-100 h-100"
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        zIndex: 5,
                        background: "rgba(255, 255, 255, 1)",
                      }}
                    >
                      <CompanyappLoader />
                    </Box>
                  )}

                  <div
                    className={`reveal ${props.preview ? "preview" : ""}`}
                    ref={setNodeRef}
                  >
                    <div className="slides">{memoizedSlides}</div>

                    {!noControls && !previewChanges && (
                      <RevealControls
                        reveal={reveal}
                        indexh={indexh}
                        presentation={presentation}
                        mode={props.preview ? "preview" : "view"}
                        showControls={showControls}
                        editMode={false}
                        isInIframe={isInIframe}
                        hasPresentationAudio={audio}
                        styles={controlStyles}
                        setDimensions={handleControlDimensions}
                        presentationAudioObject={audio}
                        isSpeakerView={isSpeakerView}
                      />
                    )}

                    {!isSpeakerView && (
                      <AudioPrompt
                        open={
                          (audio && !isLoading && !additionalLoader) ||
                          (slideHasAudioPrompt &&
                            !isLoading &&
                            !additionalLoader)
                        }
                        hasPresentationAudio={audio}
                        hasSlideAudio={slideHasAudioPrompt}
                      />
                    )}
                  </div>
                </DndContext>
              </Col>
            </Row>
          </Container>
        </main>
        </AssetSigsProvider>
      );
    }
  }
}

export default function ViewPresentation(props) {
  return (
    <ViewContextProvider>
      <ViewPresentationInner {...props} />
    </ViewContextProvider>
  );
}
